// IM-UX-002 — Análise recomendada: o InteliMove monta o escopo de PESTEL/Cinco Forças e de TAM/SAM/SOM a partir do que a
// organização JÁ registrou (perfil confirmado, ofertas, ICPs, segmentação, concorrentes, fontes, sinais, análises
// anteriores) e diz de onde veio cada valor. Regras de prioridade (determinísticas, nesta ordem):
//   1. confirmado pela equipe · 2. ativo e validado · 3. análise mais recente concluída · 4. inferido com evidência ·
//   5. padrão do InteliMove (sempre dito como padrão).
// Informação conflitante NUNCA é escolhida em silêncio: vira uma pergunta simples com as opções e o motivo.
// Nenhuma recomendação chama IA, pesquisa externa ou cálculo pesado: só leitura do que está no tenant.
import type { CreateEnvironmentRequest } from './environment.js';
import type { CreateSizingRequest, SizingAssumption, SizingMethod } from './sizing.js';

export const RECOMMENDATION_ORIGINS = ['CONFIRMADO', 'ATIVO', 'ANALISE_RECENTE', 'INFERIDO', 'PADRAO', 'ESCOLHA'] as const;
export type RecommendationOrigin = (typeof RECOMMENDATION_ORIGINS)[number];
export const RECOMMENDATION_ORIGIN_LABEL: Record<RecommendationOrigin, string> = {
  CONFIRMADO: 'confirmado pela equipe', ATIVO: 'ativo no cadastro', ANALISE_RECENTE: 'análise mais recente', INFERIDO: 'inferido com evidência',
  PADRAO: 'padrão do InteliMove', ESCOLHA: 'escolhido por você',
};
/** Prioridade da regra (menor = mais forte). ESCOLHA é a decisão explícita da pessoa e vence tudo. */
export const RECOMMENDATION_PRIORITY: Record<RecommendationOrigin, number> = { ESCOLHA: 0, CONFIRMADO: 1, ATIVO: 2, ANALISE_RECENTE: 3, INFERIDO: 4, PADRAO: 5 };

/** Um valor recomendado com a origem (regra) e a fonte concreta ("perfil confirmado da empresa, versão 2"). */
export interface RecommendedValue {
  field: string; label: string;
  /** Texto exibido (ex.: "Estado do Paraná", "12 meses", "Gestão fiscal, Folha"). */
  value: string;
  origin: RecommendationOrigin; source: string;
  /** Chave canônica do valor: a API compara com o pedido para registrar se a pessoa manteve ou alterou. */
  key: string;
  /** Recomendação feita para revisão simples (ex.: ofertas sem relação determinada com os segmentos). */
  review?: boolean;
}

export interface RecommendationConflictOption { key: string; label: string; origin: RecommendationOrigin; source: string }
/** Informações conflitantes do mesmo campo: a tela pede a escolha (sem pré-seleção) e explica o motivo. */
export interface RecommendationConflict { field: string; question: string; reason: string; options: RecommendationConflictOption[] }

export const REQUIREMENT_STATUSES = ['CONFIRMADA', 'INFERIDA', 'AUSENTE_NAO_BLOQUEANTE', 'AUSENTE_INDISPENSAVEL'] as const;
export type RequirementStatus = (typeof REQUIREMENT_STATUSES)[number];
export const REQUIREMENT_STATUS_LABEL: Record<RequirementStatus, string> = {
  CONFIRMADA: 'disponível e confirmada', INFERIDA: 'disponível, mas inferida', AUSENTE_NAO_BLOQUEANTE: 'ausente, não bloqueia', AUSENTE_INDISPENSAVEL: 'ausente e indispensável',
};
/** Saídas oferecidas quando falta informação indispensável (nunca bloqueiam a análise inteira). */
export const REQUIREMENT_ALTERNATIVES = ['INFORMAR', 'HIPOTESE', 'SO_TAM_SAM', 'REGISTRAR_LACUNA'] as const;
export type RequirementAlternative = (typeof REQUIREMENT_ALTERNATIVES)[number];
/**
 * INTELIMOVE-JRN-011 — situação adicional de um requisito, SEM criar outro vocabulário de prontidão: o `status` continua
 * sendo um dos quatro acima; a condição só precisa o porquê (parcial, revisão, conflito, desatualizado, em andamento,
 * dispensável). Campos opcionais e aditivos: as recomendações existentes não mudam.
 */
export const REQUIREMENT_CONDITIONS = ['PARCIAL', 'REVISAO_NECESSARIA', 'CONFLITO', 'DESATUALIZADO', 'EM_ANDAMENTO', 'DISPENSAVEL'] as const;
export type RequirementCondition = (typeof REQUIREMENT_CONDITIONS)[number];
export const REQUIREMENT_CONDITION_LABEL: Record<RequirementCondition, string> = {
  PARCIAL: 'parcial', REVISAO_NECESSARIA: 'precisa de revisão', CONFLITO: 'evidências conflitantes', DESATUALIZADO: 'desatualizado', EM_ANDAMENTO: 'em andamento', DISPENSAVEL: 'dispensável',
};
export interface RecommendationRequirement {
  key: string; label: string; status: RequirementStatus;
  /** O que existe (ou falta) e de onde veio. */
  detail: string;
  /** Pedido mínimo, em uma frase ("Para calcular o SOM, precisamos…"). Só quando ausente e indispensável. */
  ask?: string;
  alternatives?: RequirementAlternative[];
  /** Papel de premissa que a resposta preenche (TAM/SAM/SOM). */
  role?: 'SOM_SHARE' | 'ANNUAL_VALUE';
  /** JRN-011: por que o requisito está assim (ver REQUIREMENT_CONDITIONS). */
  condition?: RequirementCondition;
  /** JRN-011: tela existente que resolve o requisito (deep link). */
  route?: string;
  /** JRN-011: execução em andamento que vai atender o requisito (id real — IM-NFR-012). */
  runId?: string;
  /** JRN-011: quantos itens já atendem, de quantos (ex.: 2 de 3 ofertas ligadas a ICP). */
  progress?: { done: number; total: number };
}

/** Prontidão resumida (JRN-011), derivada só dos requisitos — mesma regra para todo módulo. */
export const READINESS_STATES = ['PRONTA', 'PRONTA_COM_RESSALVAS', 'NAO_PRONTA'] as const;
export type ReadinessState = (typeof READINESS_STATES)[number];
export const READINESS_STATE_LABEL: Record<ReadinessState, string> = { PRONTA: 'Pronta', PRONTA_COM_RESSALVAS: 'Pronta, com ressalvas', NAO_PRONTA: 'Ainda não está pronta' };
/**
 * NAO_PRONTA: falta algo indispensável, há conflito de evidência sem decisão ou execução ainda rodando.
 * PRONTA_COM_RESSALVAS: só faltas não bloqueantes, itens inferidos, revisão, parcial ou desatualizado.
 * Requisito DISPENSAVEL ausente não pesa.
 */
export function readinessStateOf(reqs: readonly RecommendationRequirement[]): ReadinessState {
  if (reqs.some((r) => r.status === 'AUSENTE_INDISPENSAVEL' || r.condition === 'CONFLITO' || r.condition === 'EM_ANDAMENTO')) return 'NAO_PRONTA';
  if (reqs.some((r) => (r.status !== 'CONFIRMADA' && r.condition !== 'DISPENSAVEL') || (r.condition && r.condition !== 'DISPENSAVEL'))) return 'PRONTA_COM_RESSALVAS';
  return 'PRONTA';
}
export interface ReadinessDto {
  generatedAt: string;
  state: ReadinessState;
  requirements: RecommendationRequirement[];
  /** Nada é gerado, pesquisado nem coletado por esta leitura. */
  limitations: string[];
}
/** IM-API-325 — prontidão para segmentar o mercado (módulo de segmentação). */
export interface SegmentationReadinessDto extends ReadinessDto {
  latest: { id: string; name: string; versionNo: number; contextVersion: number | null; updatedAt: string } | null;
}
/** IM-API-326 — prontidão de battlecards, por concorrente confirmado (módulo de battlecards). */
export interface BattlecardReadinessDto extends ReadinessDto {
  competitors: Array<{ id: string; name: string; battlecardId: string | null; state: ReadinessState; requirements: RecommendationRequirement[] }>;
}

export type ExpectedConfidence = 'ALTA' | 'MEDIA' | 'BAIXA';
interface RecommendationBase {
  generatedAt: string;
  values: RecommendedValue[];
  conflicts: RecommendationConflict[];
  requirements: RecommendationRequirement[];
  expectedConfidence: ExpectedConfidence; confidenceReason: string;
  limitations: string[];
  contextConfirmed: boolean;
}

/** IM-API-235 — recomendação de PESTEL e Cinco Forças. */
export interface EnvironmentRecommendationDto extends RecommendationBase {
  request: CreateEnvironmentRequest;
  /** Matéria-prima disponível no período recomendado (contagens, nada inventado). */
  basis: { signals: number; publications: number; marketFindings: number; competitors: number; competitorsResearched: number; sourcesActive: number; previousAnalyses: number };
}

/** Premissa de outra estimativa que pode ser reaproveitada (CONFIRMADA = dado interno/oficial/público; INFERIDA = hipótese). */
export interface ReusableAssumption { assumption: SizingAssumption; from: { id: string; name: string; versionNo: number }; status: 'CONFIRMADA' | 'INFERIDA' }

/** IM-API-210 — recomendação de TAM, SAM e SOM. */
export interface SizingRecommendationDto extends RecommendationBase {
  request: CreateSizingRequest;
  method: { method: SizingMethod; origin: 'AUTOMATICA' | 'PADRAO'; reason: string };
  /** Premissas reaproveitadas que entram no pedido recomendado (já em `request.assumptions`). */
  reused: ReusableAssumption[];
  /** Premissa de cada opção dos conflitos de premissa (chave da opção → premissa). */
  conflictAssumptions: Record<string, SizingAssumption>;
  /** CNAE que entrarão no escopo e de onde vêm (o servidor os resolve pelos ICPs e segmentos; nada é inferido sem base). */
  cnae: Array<{ code: string; origin: string; status: 'CONFIRMADA' | 'INFERIDA' }>;
  officialAvailable: boolean;
}

/** Origem registrada na criação: o que a pessoa manteve da recomendação e o que alterou (gravado no histórico). */
export interface ProvenanceEntry { field: string; label: string; value: string; origin: RecommendationOrigin; source: string }
export interface CreationProvenance { mode: 'RECOMENDADA' | 'PERSONALIZADA'; entries: ProvenanceEntry[] }

/** Compara o pedido com a recomendação: campo igual mantém a origem da regra; diferente vira "escolhido por você". */
export function provenanceOf(values: RecommendedValue[], actual: Record<string, { key: string; value: string }>): CreationProvenance {
  const entries: ProvenanceEntry[] = [];
  let changed = false;
  for (const v of values) {
    const a = actual[v.field];
    if (!a) continue;
    if (a.key === v.key) entries.push({ field: v.field, label: v.label, value: v.value, origin: v.origin, source: v.source });
    else { changed = true; entries.push({ field: v.field, label: v.label, value: a.value, origin: 'ESCOLHA', source: `alterado em “Personalizar análise” (recomendado: ${v.value})`.slice(0, 300) }); }
  }
  for (const [field, a] of Object.entries(actual)) {
    if (values.some((v) => v.field === field)) continue;
    if (a.key) { changed = true; entries.push({ field, label: field, value: a.value, origin: 'ESCOLHA', source: 'informado por você' }); }
  }
  return { mode: changed ? 'PERSONALIZADA' : 'RECOMENDADA', entries };
}

/** Chave canônica de listas de ids (ordem não importa). */
export const idsKey = (ids: readonly string[] | null | undefined) => [...(ids ?? [])].sort().join(',');

/** Chaves canônicas do pedido de PESTEL/Cinco Forças (mesma regra na recomendação e na criação). */
export function environmentFieldKeys(r: CreateEnvironmentRequest, names: { segments?: Map<string, string>; offerings?: Map<string, string>; segmentation?: string | null } = {}): Record<string, { key: string; value: string }> {
  const nm = (ids: string[], m?: Map<string, string>) => ids.map((id) => m?.get(id) ?? id).join(', ') || 'nenhum';
  return {
    name: { key: r.name.trim(), value: r.name.trim() },
    region: { key: (r.scope.region?.text ?? '').trim().toLowerCase() || 'brasil', value: r.scope.region?.text?.trim() || 'Brasil' },
    window: { key: String(r.scope.windowDays), value: windowLabel(r.scope.windowDays) },
    segmentation: { key: r.scope.segmentationId ?? '', value: names.segmentation ?? (r.scope.segmentationId ? r.scope.segmentationId : 'nenhuma') },
    segments: { key: idsKey(r.scope.segmentIds), value: nm(r.scope.segmentIds, names.segments) },
    offerings: { key: idsKey(r.scope.offeringIds), value: r.scope.offeringIds.length ? nm(r.scope.offeringIds, names.offerings) : 'todas as confirmadas' },
  };
}

/** Chaves canônicas do pedido de TAM/SAM/SOM. */
export function sizingFieldKeys(r: CreateSizingRequest, names: { offerings?: Map<string, string>; icps?: Map<string, string>; segments?: Map<string, string>; segmentation?: string | null } = {}): Record<string, { key: string; value: string }> {
  const nm = (ids: string[], m?: Map<string, string>) => ids.map((id) => m?.get(id) ?? id).join(', ') || 'nenhum';
  const roles = (r.assumptions ?? []).filter((a) => a.enabled !== false).map((a) => `${a.role}:${a.likely}`).sort();
  return {
    name: { key: r.name.trim(), value: r.name.trim() },
    region: { key: (r.scope.region?.text ?? '').trim().toLowerCase() || 'brasil', value: r.scope.region?.text?.trim() || 'Brasil' },
    method: { key: r.method, value: r.method },
    currency: { key: r.currency, value: r.currency },
    period: { key: `${r.period.label}|${r.period.start ?? ''}|${r.period.end ?? ''}`, value: r.period.label },
    offerings: { key: idsKey(r.scope.offeringIds), value: nm(r.scope.offeringIds, names.offerings) },
    icps: { key: idsKey(r.scope.icpIds), value: nm(r.scope.icpIds, names.icps) },
    segmentation: { key: r.scope.segmentationId ?? '', value: names.segmentation ?? (r.scope.segmentationId ? r.scope.segmentationId : 'nenhuma') },
    segments: { key: idsKey(r.scope.segmentIds), value: nm(r.scope.segmentIds, names.segments) },
    cnae: { key: idsKey(r.scope.cnaeCodes), value: r.scope.cnaeCodes.join(', ') || 'os dos ICPs e segmentos' },
    assumptions: { key: roles.join(','), value: roles.length ? `${roles.length} premissa(s) da equipe` : 'nenhuma premissa da equipe' },
    official: { key: String(r.fetchOfficial), value: r.fetchOfficial ? 'consultar IBGE' : 'não consultar IBGE' },
  };
}

export const windowLabel = (days: number) => (days % 365 === 0 ? `${days / 365 === 1 ? '12 meses' : `${(days / 365) * 12} meses`}` : days % 30 === 0 ? `${days / 30} meses` : `${days} dias`);
